import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import {
    ArrowLeft,
    FileText,
    Download,
    Eye,
    Loader2,
    Settings,
    Palette,
    Save,
    History,
    Award,
    Truck,
    Cpu,
    CheckCircle2,
    XCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CertificadosPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    
    // Tipos disponibles
    const [tiposCertificado, setTiposCertificado] = useState([]);
    const [tiposDispositivo, setTiposDispositivo] = useState([]);
    
    // Configuración actual
    const [tipoSeleccionado, setTipoSeleccionado] = useState('instalacion');
    const [dispositivoTipo, setDispositivoTipo] = useState('gps');
    const [vehiculoId, setVehiculoId] = useState('');
    const [vehiculos, setVehiculos] = useState([]);
    
    // Campos adicionales
    const [camposAdicionales, setCamposAdicionales] = useState({});
    
    // Estilo personalizado
    const [estilo, setEstilo] = useState({
        color_primario: '#1E3A8A',
        color_secundario: '#3B82F6',
        color_texto: '#1F2937',
        tamano_titulo: 24,
        tamano_subtitulo: 14,
        tamano_cuerpo: 11,
        mostrar_logo: true,
        mostrar_qr: true,
        mostrar_firma: true,
        orientacion: 'portrait'
    });
    
    // Historial
    const [historial, setHistorial] = useState([]);
    const [showHistorial, setShowHistorial] = useState(false);
    
    // Preview
    const [previewPdf, setPreviewPdf] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const getToken = () => localStorage.getItem('token');

    const loadData = async () => {
        try {
            const [tiposRes, vehiculosRes, historialRes] = await Promise.all([
                fetch(`${API_URL}/api/certificados/tipos`, {
                    headers: { 'Authorization': `Bearer ${getToken()}` }
                }),
                fetch(`${API_URL}/api/ordenes/vehiculos?limit=100`, {
                    headers: { 'Authorization': `Bearer ${getToken()}` }
                }),
                fetch(`${API_URL}/api/certificados/historial?limit=20`, {
                    headers: { 'Authorization': `Bearer ${getToken()}` }
                })
            ]);

            const tiposData = await tiposRes.json();
            setTiposCertificado(tiposData.tipos || []);
            setTiposDispositivo(tiposData.tipos_dispositivo || []);

            const vehiculosData = await vehiculosRes.json();
            setVehiculos(Array.isArray(vehiculosData) ? vehiculosData : vehiculosData.vehiculos || []);

            const historialData = await historialRes.json();
            setHistorial(Array.isArray(historialData) ? historialData : []);

        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleCampoChange = (campo, valor) => {
        setCamposAdicionales(prev => ({ ...prev, [campo]: valor }));
    };

    const handleEstiloChange = (campo, valor) => {
        setEstilo(prev => ({ ...prev, [campo]: valor }));
    };

    const generarPreview = async () => {
        setGenerating(true);
        try {
            const res = await fetch(`${API_URL}/api/certificados/preview`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getToken()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    tipo: tipoSeleccionado,
                    vehiculo_id: vehiculoId || null,
                    dispositivo_tipo: dispositivoTipo,
                    campos_adicionales: camposAdicionales,
                    estilo
                })
            });

            if (!res.ok) throw new Error('Error al generar preview');

            const data = await res.json();
            setPreviewPdf(data.pdf_base64);
            setShowPreview(true);
        } catch (err) {
            toast.error('Error al generar vista previa');
        } finally {
            setGenerating(false);
        }
    };

    const generarCertificado = async () => {
        setGenerating(true);
        try {
            const res = await fetch(`${API_URL}/api/certificados/generar`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getToken()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    tipo: tipoSeleccionado,
                    vehiculo_id: vehiculoId || null,
                    dispositivo_tipo: dispositivoTipo,
                    campos_adicionales: camposAdicionales,
                    estilo
                })
            });

            if (!res.ok) throw new Error('Error al generar certificado');

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `certificado_${tipoSeleccionado}_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            toast.success('Certificado generado y descargado');
            loadData(); // Recargar historial
        } catch (err) {
            toast.error('Error al generar certificado');
        } finally {
            setGenerating(false);
        }
    };

    const renderCamposAdicionales = () => {
        switch (tipoSeleccionado) {
            case 'instalacion':
                return (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Marca GPS</Label>
                                <Input
                                    value={camposAdicionales.marca_gps || ''}
                                    onChange={(e) => handleCampoChange('marca_gps', e.target.value)}
                                    placeholder="Ej: Teltonika"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Modelo GPS</Label>
                                <Input
                                    value={camposAdicionales.modelo_gps || ''}
                                    onChange={(e) => handleCampoChange('modelo_gps', e.target.value)}
                                    placeholder="Ej: FMB120"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>IMEI</Label>
                            <Input
                                value={camposAdicionales.imei || ''}
                                onChange={(e) => handleCampoChange('imei', e.target.value)}
                                placeholder="Ej: 352093089012345"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Fecha de Instalación</Label>
                            <Input
                                type="date"
                                value={camposAdicionales.fecha_instalacion || ''}
                                onChange={(e) => handleCampoChange('fecha_instalacion', e.target.value)}
                            />
                        </div>
                    </div>
                );
            
            case 'operatividad':
                return (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Estado del Dispositivo</Label>
                            <Select
                                value={camposAdicionales.estado || 'OPERATIVO'}
                                onValueChange={(v) => handleCampoChange('estado', v)}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="OPERATIVO">
                                        <span className="flex items-center gap-2">
                                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                                            Operativo
                                        </span>
                                    </SelectItem>
                                    <SelectItem value="CON_FALLAS">
                                        <span className="flex items-center gap-2">
                                            <XCircle className="h-4 w-4 text-red-500" />
                                            Con Fallas
                                        </span>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Señal GPS</Label>
                                <Select
                                    value={camposAdicionales.senal_gps || 'OK'}
                                    onValueChange={(v) => handleCampoChange('senal_gps', v)}
                                >
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="OK">OK</SelectItem>
                                        <SelectItem value="DEBIL">Débil</SelectItem>
                                        <SelectItem value="SIN_SENAL">Sin Señal</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Transmisión</Label>
                                <Select
                                    value={camposAdicionales.transmision || 'OK'}
                                    onValueChange={(v) => handleCampoChange('transmision', v)}
                                >
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="OK">OK</SelectItem>
                                        <SelectItem value="INTERMITENTE">Intermitente</SelectItem>
                                        <SelectItem value="FALLA">Falla</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Fecha de Verificación</Label>
                            <Input
                                type="date"
                                value={camposAdicionales.fecha_verificacion || ''}
                                onChange={(e) => handleCampoChange('fecha_verificacion', e.target.value)}
                            />
                        </div>
                    </div>
                );
            
            case 'desinstalacion':
                return (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Motivo de Desinstalación</Label>
                            <Select
                                value={camposAdicionales.motivo || 'Solicitud del cliente'}
                                onValueChange={(v) => handleCampoChange('motivo', v)}
                            >
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Solicitud del cliente">Solicitud del cliente</SelectItem>
                                    <SelectItem value="Venta del vehículo">Venta del vehículo</SelectItem>
                                    <SelectItem value="Cambio de dispositivo">Cambio de dispositivo</SelectItem>
                                    <SelectItem value="Fin de contrato">Fin de contrato</SelectItem>
                                    <SelectItem value="Otro">Otro</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Fecha de Desinstalación</Label>
                            <Input
                                type="date"
                                value={camposAdicionales.fecha_desinstalacion || ''}
                                onChange={(e) => handleCampoChange('fecha_desinstalacion', e.target.value)}
                            />
                        </div>
                    </div>
                );
            
            case 'kilometraje':
                return (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Período</Label>
                            <Input
                                value={camposAdicionales.periodo || ''}
                                onChange={(e) => handleCampoChange('periodo', e.target.value)}
                                placeholder="Ej: Marzo 2026"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Kilometraje Inicial</Label>
                                <Input
                                    type="number"
                                    value={camposAdicionales.km_inicial || ''}
                                    onChange={(e) => handleCampoChange('km_inicial', e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Kilometraje Final</Label>
                                <Input
                                    type="number"
                                    value={camposAdicionales.km_final || ''}
                                    onChange={(e) => handleCampoChange('km_final', e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Total Recorrido (km)</Label>
                            <Input
                                type="number"
                                value={camposAdicionales.km_recorridos || ''}
                                onChange={(e) => handleCampoChange('km_recorridos', e.target.value)}
                                placeholder="0"
                            />
                        </div>
                    </div>
                );
            
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <div className="grid md:grid-cols-2 gap-6">
                    <Skeleton className="h-96" />
                    <Skeleton className="h-96" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Award className="h-6 w-6" />
                            Certificados
                        </h1>
                        <p className="text-muted-foreground">Genera certificados profesionales con diseño personalizable</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowHistorial(true)}>
                        <History className="h-4 w-4 mr-2" />
                        Historial
                    </Button>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                {/* Configuración */}
                <div className="space-y-4">
                    <Tabs defaultValue="datos" className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="datos">
                                <FileText className="h-4 w-4 mr-2" />
                                Datos
                            </TabsTrigger>
                            <TabsTrigger value="estilo">
                                <Palette className="h-4 w-4 mr-2" />
                                Estilo
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="datos" className="space-y-4">
                            {/* Tipo de Certificado */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base">Tipo de Certificado</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-2 gap-2">
                                        {tiposCertificado.map(tipo => (
                                            <Button
                                                key={tipo.codigo}
                                                variant={tipoSeleccionado === tipo.codigo ? "default" : "outline"}
                                                className="h-auto py-3 flex flex-col items-start"
                                                onClick={() => {
                                                    setTipoSeleccionado(tipo.codigo);
                                                    setCamposAdicionales({});
                                                }}
                                            >
                                                <span className="font-medium">{tipo.nombre.replace('Certificado de ', '')}</span>
                                                <span className="text-xs text-muted-foreground line-clamp-1">
                                                    {tipo.descripcion}
                                                </span>
                                            </Button>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Tipo de Dispositivo */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Cpu className="h-4 w-4" />
                                        Tipo de Dispositivo
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <Select value={dispositivoTipo} onValueChange={setDispositivoTipo}>
                                        <SelectTrigger data-testid="device-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {tiposDispositivo.map(tipo => (
                                                <SelectItem key={tipo.codigo} value={tipo.codigo}>
                                                    <span className="flex items-center gap-2">
                                                        <span>{tipo.icono}</span>
                                                        {tipo.nombre}
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </CardContent>
                            </Card>

                            {/* Vehículo */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Truck className="h-4 w-4" />
                                        Vehículo (Opcional)
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <Select value={vehiculoId || "none"} onValueChange={(v) => setVehiculoId(v === "none" ? "" : v)}>
                                        <SelectTrigger data-testid="vehicle-select">
                                            <SelectValue placeholder="Seleccionar vehículo..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Sin vehículo (datos de prueba)</SelectItem>
                                            {vehiculos.map(v => (
                                                <SelectItem key={v.id} value={v.id}>
                                                    {v.patente} - {v.marca} {v.modelo}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </CardContent>
                            </Card>

                            {/* Campos Adicionales */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base">Datos del Certificado</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {renderCamposAdicionales()}
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="estilo" className="space-y-4">
                            {/* Colores */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base">Colores</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label>Primario</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    type="color"
                                                    value={estilo.color_primario}
                                                    onChange={(e) => handleEstiloChange('color_primario', e.target.value)}
                                                    className="w-12 h-10 p-1"
                                                />
                                                <Input
                                                    value={estilo.color_primario}
                                                    onChange={(e) => handleEstiloChange('color_primario', e.target.value)}
                                                    className="flex-1"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Secundario</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    type="color"
                                                    value={estilo.color_secundario}
                                                    onChange={(e) => handleEstiloChange('color_secundario', e.target.value)}
                                                    className="w-12 h-10 p-1"
                                                />
                                                <Input
                                                    value={estilo.color_secundario}
                                                    onChange={(e) => handleEstiloChange('color_secundario', e.target.value)}
                                                    className="flex-1"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Texto</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    type="color"
                                                    value={estilo.color_texto}
                                                    onChange={(e) => handleEstiloChange('color_texto', e.target.value)}
                                                    className="w-12 h-10 p-1"
                                                />
                                                <Input
                                                    value={estilo.color_texto}
                                                    onChange={(e) => handleEstiloChange('color_texto', e.target.value)}
                                                    className="flex-1"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Tamaños */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base">Tamaños de Fuente</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label>Título</Label>
                                            <Input
                                                type="number"
                                                value={estilo.tamano_titulo}
                                                onChange={(e) => handleEstiloChange('tamano_titulo', parseInt(e.target.value))}
                                                min={12}
                                                max={36}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Subtítulo</Label>
                                            <Input
                                                type="number"
                                                value={estilo.tamano_subtitulo}
                                                onChange={(e) => handleEstiloChange('tamano_subtitulo', parseInt(e.target.value))}
                                                min={10}
                                                max={24}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Cuerpo</Label>
                                            <Input
                                                type="number"
                                                value={estilo.tamano_cuerpo}
                                                onChange={(e) => handleEstiloChange('tamano_cuerpo', parseInt(e.target.value))}
                                                min={8}
                                                max={16}
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Opciones */}
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base">Opciones</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <Label>Mostrar Logo</Label>
                                        <Checkbox
                                            checked={estilo.mostrar_logo}
                                            onCheckedChange={(v) => handleEstiloChange('mostrar_logo', v)}
                                        />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <Label>Mostrar QR de Verificación</Label>
                                        <Checkbox
                                            checked={estilo.mostrar_qr}
                                            onCheckedChange={(v) => handleEstiloChange('mostrar_qr', v)}
                                        />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <Label>Mostrar Sección de Firmas</Label>
                                        <Checkbox
                                            checked={estilo.mostrar_firma}
                                            onCheckedChange={(v) => handleEstiloChange('mostrar_firma', v)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Orientación</Label>
                                        <Select
                                            value={estilo.orientacion}
                                            onValueChange={(v) => handleEstiloChange('orientacion', v)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="portrait">Vertical</SelectItem>
                                                <SelectItem value="landscape">Horizontal</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </div>

                {/* Preview y Acciones */}
                <div className="space-y-4">
                    <Card className="h-full">
                        <CardHeader>
                            <CardTitle>Vista Previa</CardTitle>
                            <CardDescription>
                                El certificado se generará con los datos configurados
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Preview Area */}
                            <div 
                                className="border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center min-h-[300px]"
                                style={{ 
                                    borderColor: estilo.color_primario,
                                    backgroundColor: `${estilo.color_primario}10`
                                }}
                            >
                                <div className="text-center space-y-4">
                                    <div
                                        className="text-2xl font-bold"
                                        style={{ color: estilo.color_primario }}
                                    >
                                        {tiposCertificado.find(t => t.codigo === tipoSeleccionado)?.nombre || 'Certificado'}
                                    </div>
                                    <div
                                        className="text-lg"
                                        style={{ color: estilo.color_secundario }}
                                    >
                                        {tiposDispositivo.find(t => t.codigo === dispositivoTipo)?.icono}{' '}
                                        {tiposDispositivo.find(t => t.codigo === dispositivoTipo)?.nombre || 'Dispositivo'}
                                    </div>
                                    {vehiculoId && vehiculos.find(v => v.id === vehiculoId) && (
                                        <div className="text-muted-foreground">
                                            Vehículo: {vehiculos.find(v => v.id === vehiculoId)?.patente}
                                        </div>
                                    )}
                                    <div className="flex gap-4 justify-center text-xs text-muted-foreground">
                                        {estilo.mostrar_logo && <span>Logo</span>}
                                        {estilo.mostrar_qr && <span>QR</span>}
                                        {estilo.mostrar_firma && <span>Firmas</span>}
                                    </div>
                                </div>
                            </div>

                            {/* Botones de Acción */}
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    className="flex-1"
                                    onClick={generarPreview}
                                    disabled={generating}
                                    data-testid="preview-btn"
                                >
                                    {generating ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Eye className="h-4 w-4 mr-2" />
                                    )}
                                    Vista Previa PDF
                                </Button>
                                <Button
                                    className="flex-1"
                                    onClick={generarCertificado}
                                    disabled={generating}
                                    data-testid="generate-btn"
                                >
                                    {generating ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Download className="h-4 w-4 mr-2" />
                                    )}
                                    Generar y Descargar
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Dialog de Preview */}
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="max-w-4xl max-h-[90vh]">
                    <DialogHeader>
                        <DialogTitle>Vista Previa del Certificado</DialogTitle>
                    </DialogHeader>
                    {previewPdf && (
                        <iframe
                            src={`data:application/pdf;base64,${previewPdf}`}
                            className="w-full h-[70vh] border rounded"
                            title="Preview PDF"
                        />
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPreview(false)}>
                            Cerrar
                        </Button>
                        <Button onClick={generarCertificado} disabled={generating}>
                            <Download className="h-4 w-4 mr-2" />
                            Descargar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog de Historial */}
            <Dialog open={showHistorial} onOpenChange={setShowHistorial}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Historial de Certificados</DialogTitle>
                    </DialogHeader>
                    <div className="max-h-[60vh] overflow-y-auto">
                        {historial.length === 0 ? (
                            <p className="text-center text-muted-foreground py-8">
                                No hay certificados generados
                            </p>
                        ) : (
                            <div className="space-y-2">
                                {historial.map((cert, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-center justify-between p-3 bg-muted/50 rounded"
                                    >
                                        <div>
                                            <p className="font-medium">
                                                {tiposCertificado.find(t => t.codigo === cert.tipo)?.nombre || cert.tipo}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                {new Date(cert.created_at).toLocaleString('es-CL')}
                                            </p>
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                            {cert.dispositivo_tipo}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
